/**
 * Admin customization config for the Keyword Benchmark page (POC).
 *
 * The admin page (`/admin/explore/keyword-benchmarking`) writes this config to
 * localStorage; both the admin preview and the public page (on the same
 * browser) read it and apply it to what the benchmark view renders. There is
 * no backend — this is a proof of concept.
 *
 * Applies only to the entity benchmark modes (institution / researcher / unit /
 * venue), never the Research Topics overview.
 */
import {
  BENCHMARK_METRIC_GROUPS,
  BENCHMARK_METRIC_SECTIONS,
  BENCHMARK_SUB_COLS,
  filterMetricsByMode,
} from "./benchmarkMetricConfig";

export const ADMIN_CONFIG_STORAGE_KEY = "kw_benchmark_admin_config";

export const SCALING_VIEW_OPTIONS = [
  { value: "bar", label: "Bar chart" },
  { value: "pie", label: "Pie chart" },
  { value: "table", label: "Table" },
];

export const OVERTIME_OPTIONS = [
  { value: "citations", label: "Citations over time" },
  { value: "publications", label: "Publications over time" },
  { value: "grants", label: "Grants over time" },
];

export const PALETTE_OPTIONS = [
  { value: "default", label: "Default" },
  { value: "colorblind", label: "Colorblind-safe" },
  { value: "warm", label: "Warm" },
  { value: "cool", label: "Cool" },
  { value: "grayscale", label: "Grayscale" },
];

// Which config array each metric section maps to, plus its selection bounds.
export const SECTION_CONFIG = {
  [BENCHMARK_METRIC_GROUPS.IMPACT]: { key: "citationImpact", min: 1, max: 4 },
  [BENCHMARK_METRIC_GROUPS.CAPACITY]: { key: "capacity", min: 0, max: 2 },
  [BENCHMARK_METRIC_GROUPS.MOMENTUM]: { key: "momentum", min: 0, max: 2 },
};

export const OVERTIME_BOUNDS = { min: 0, max: 3 };
export const BREAKDOWN_BOUNDS = { min: 1, max: 6 };

/** Metric-key options for a given section, scoped to the current mode. */
export const getSectionOptions = (sectionId, mode) => {
  const section = BENCHMARK_METRIC_SECTIONS.find((s) => s.id === sectionId);
  if (!section) return [];
  return filterMetricsByMode(section.stats, mode).map((m) => ({
    value: m.key,
    label: m.label,
  }));
};

/** Metric-key options for the breakdown table, scoped to the current mode. */
export const getBreakdownOptions = (mode) =>
  filterMetricsByMode(BENCHMARK_SUB_COLS, mode).map((c) => ({
    value: c.key,
    label: c.label,
  }));

export const DEFAULT_ADMIN_CONFIG = {
  scalingView: "bar",
  citationImpact: [
    "h_index",
    "i10_index",
    "median_citations_per_paper",
    "percent_papers_top_10",
  ],
  capacity: ["publication_count_per_faculty", "active_researcher_count"],
  momentum: ["specialization_index", "publication_cagr"],
  showHeatmap: true,
  showRelatedConcepts: true,
  overTime: ["citations", "publications", "grants"],
  breakdown: [
    "publication_count",
    "citation_count",
    "h_index",
    "faculty_count",
    "grants_count",
    "publication_count_per_faculty",
  ],
  // Chart colors: a named base palette plus optional per-entity overrides
  // (keyed by entity id → hex).
  palette: "default",
  entityColors: {},
};

export const mergeAdminConfig = (config) => ({
  ...DEFAULT_ADMIN_CONFIG,
  ...(config && typeof config === "object" ? config : {}),
  entityColors: {
    ...(config && typeof config.entityColors === "object"
      ? config.entityColors
      : {}),
  },
});

/** Load the saved config merged over defaults, or null if nothing is saved. */
export const loadAdminConfig = () => {
  try {
    const raw = window.localStorage.getItem(ADMIN_CONFIG_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_ADMIN_CONFIG, ...parsed };
  } catch {
    return null;
  }
};

export const saveAdminConfig = (config) => {
  try {
    window.localStorage.setItem(
      ADMIN_CONFIG_STORAGE_KEY,
      JSON.stringify(config)
    );
  } catch {
    /* ignore quota / serialization errors in this POC */
  }
};
